import { urlToURL } from "../../lib/utils/domains";
import { fileNameSafeString } from "../../lib/utils/fileNameSafeString";
import { MyDataGoWhereEventArchiveFormat, DbRecording, eventsFileFormatVersion, UUID, isMyDataGoWhereEventArchiveFormat, getRangeForAllRecordsWithRecordingUuidAndRowUuid } from "./DatabaseTypes";
import { DbEventsTableReader } from "./DbEventsTableReader";
import { DbMhtmlCaptureTableReader } from "./DbMhtmlCaptureTableReader";
import { DbVideoFragmentTableReader } from "./DbVideoFragmentTableReader";
import { ExtensionSettingsAccessor, getDirectoryToSaveInto } from "./DbExtensionSettings";
import { DbRecordingEntryReader } from "./DbRecordingEntryReader";

export interface FileNameSignaturePair {
	readonly fileName: string;
	readonly signature: string;
}

export const signArrayBufferED25519 = async (cryptoKey: CryptoKey, arrayBuffer: ArrayBuffer) => {
	const signature = await crypto.subtle.sign("ED25519", cryptoKey, arrayBuffer);
	const signatureHex = Array.from(new Uint8Array(signature)).map(byte => byte.toString(16).padStart(2, '0')).join('');
	return signatureHex;
}

export const signFileED25519 = async (cryptoKey: CryptoKey, fileSystemHandle: FileSystemFileHandle) => {
	return await signArrayBufferED25519(cryptoKey, await (await fileSystemHandle.getFile()).arrayBuffer());
}

export const recordingToArchive = async (recordingUuid: UUID) => {
	const recording = await (await requireEvidenceDatabase()).get("recordings", recordingUuid);
	if (recording == null) return;
	const events = await (await DbEventsTableReader.forRecordingUuid(recordingUuid)).getEvents();
	return {
		version: eventsFileFormatVersion,
		...recording,
		events,
	} satisfies MyDataGoWhereEventArchiveFormat;
}


const writeFileByName = async (
	directoryHandle: FileSystemDirectoryHandle,
	fileName: string,
	data: string | ArrayBuffer
) => {
	const fileSystemHandle = await directoryHandle.getFileHandle(fileName, { create: true });
	const writeStream = await fileSystemHandle.createWritable({ keepExistingData: false });
	await writeStream.write(data);
	await writeStream.close();
}

const writeFileByNameAndSign = async (
	directoryHandle: FileSystemDirectoryHandle,
	fileName: string,
	data: string | ArrayBuffer,
	cryptoKey: CryptoKey | undefined
): Promise<FileNameSignaturePair[]> => {
	await writeFileByName(directoryHandle, fileName, data);
	if (cryptoKey == null) return [];
	const signature = await signArrayBufferED25519(cryptoKey, typeof data === "string" ? new TextEncoder().encode(data).buffer as ArrayBuffer : data);
	return [{ fileName, signature } satisfies FileNameSignaturePair];
}

export const writeHarsLogToHarFile = async (
	directoryHandle: FileSystemDirectoryHandle,
	baseName: string,
	recordingUuid: UUID,
	cryptoKey: CryptoKey | undefined = undefined
): Promise<FileNameSignaturePair[]> => {
	const db = await requireEvidenceDatabase();
	const harLogs = await db.getAll("harLogs", 		getRangeForAllRecordsWithRecordingUuidAndRowUuid(recordingUuid));
	return await Promise.all(harLogs.map( async harLog => {
			const har: Har = {log: harLog};
			const harJson = JSON.stringify(har, undefined, "\t").replaceAll("\n", "\n\t");
			return (await writeFileByNameAndSign(directoryHandle, `${fileNameSafeString(`${baseName}-${harLog.whenRecordedIso}`)}.har`, harJson, cryptoKey))[0]!;
	}));
}

export const writeRecordingToArchiveFile = async (
	licenseCache: LicenseCache,
	directoryHandle: FileSystemDirectoryHandle,
	baseName: string,
	recording: DbRecording,
	cryptoKey: CryptoKey | undefined = undefined
): Promise<FileNameSignaturePair[]> => {
	const db = await requireEvidenceDatabase();

	if (licenseCache == null || licenseCache.mayExportData == false) {
		return [];
	}
	console.log(`LicensesCache`, licenseCache);
	const fileName = `${fileNameSafeString(baseName)} Events.json`;
	const fileSystemHandle = await directoryHandle.getFileHandle(fileName, { create: true });
	const writeStream = await fileSystemHandle.createWritable({ keepExistingData: false });

	const eventsFileFormatHeader = `{
	"version": ${eventsFileFormatVersion},${
			recording.name == null ? '' : `
	"name": ${JSON.stringify(recording.name)},`}
	"searchTermsNewlineSeparated": ${JSON.stringify(recording.searchTermsNewlineSeparated ?? "", undefined, "\t").replaceAll("\n", "\n\t")},
	"globalSearchTermsNewlineSeparated": ${JSON.stringify(recording.globalSearchTermsNewlineSeparated ?? "", undefined, "\t").replaceAll("\n", "\n\t")},
	"recordingUuid": ${JSON.stringify(recording.recordingUuid)},
	"startTimeIso": ${JSON.stringify(recording.startTimeIso)},
	"settings": ${JSON.stringify(recording.settings, undefined, "\t").replaceAll("\n", "\n\t\t")},
	"dataSummary": ${JSON.stringify(recording.dataSummary ?? {}, undefined, "\t")?.replaceAll("\n", "\n\t\t")},
	"events": [`
	
	await writeStream.write(eventsFileFormatHeader);
	let first = true;
	await (await DbEventsTableReader.forRecordingUuid(recording.recordingUuid)).forEvents(0, 
		async (event) => {
			if (isNetworkEvent(event)) {
				if (!licenseCache.mayExportDataAt({pageUrl: event.tab.url})) {
					// If the license does not allow exporting data for this page, skip this event
					return;
				}
			}
			await writeStream.write(
				// add comma separator before all events but the first
				(first ? '' : ",") +
				// stringify using tab indentation
				JSON.stringify(replaceArrayBuffersWithStrings(event), null, "\t")
					// shift stringified result two tabs to the right since
					// there are two levels of indentation in the file format
					.replaceAll("\n", "\n\t")
			);
			first = false;
		}
	);
	await writeStream.write(`\],\n`);
	const har = await db.get("harLogs", IDBKeyRange.only(recording.recordingUuid));
	ProcessInfo.log(`Writing recording to archive file with har with ${har?.entries.length ?? 0} entries`);
	if (har != null) {
		const harJson = JSON.stringify(har, undefined, "\t").replaceAll("\n", "\n\t");
		await writeStream.write(`	"har": `);
		await writeStream.write(harJson);
		await writeStream.write(',\n');
	}
	const harEntries = await db.getAll("harEntries", IDBKeyRange.bound([recording.recordingUuid, UUID.min], [recording.recordingUuid, UUID.max], false, false));
	ProcessInfo.log(`Writing ${harEntries.length} raw har entries`);
	if (harEntries != null) {
		await writeStream.write(`	"harEntries": [`);
		let first = true;
		for (const harEntry of harEntries) {
			await writeStream.write(`${first ? '' : ','}\n\t` + JSON.stringify(harEntry, undefined, "\t").replaceAll("\n", "\n\t"));
			first = false;
		}
		await writeStream.write('\n\t],\n');
	}
	await writeStream.write(`\t"emptyFieldSoThatNoLingeringCommasExist": true\n}\n`);
	await writeStream.close();
	return cryptoKey == null ? [] : [{
		fileName,
		signature: await signFileED25519(cryptoKey, fileSystemHandle)
	} satisfies FileNameSignaturePair];
}

export const importRecordingFromJsonStream = async (
	stream: ReadableStream<Uint8Array>, onProgressUpdate?:
	(eventsRead: number) => void
): Promise<void> => {
	const keys = [
		"recordingUuid",
		"version",
		"status",
		"startTimeIso",
		"stopTimeIso",
		"name",
		"callStackResolution",
		"externallyVisibleIpAddress",
		"videoRecordingInProgress",
		"searchTermsNewlineSeparated",
		"globalSearchTermsNewlineSeparated",
		"domGeneratedSearchTermsToIgnore",
		"settings"
	] as const satisfies (keyof MyDataGoWhereEventArchiveFormat)[];
	ProcessInfo.log(`Starting to parse archive from stream with keys ${keys.join(", ")}`);
	const jsonParser = new JSONParser({ paths: ["$"] });
	for await (const chunk of stream.pipeThrough(jsonParser)) {
		if (isMyDataGoWhereEventArchiveFormat(chunk.value)) {
			await importRecordingAndDbEventsFromArchive(chunk.value);
		} else{
			ProcessInfo.errorLog("Parsed JSON that does not match expected format", chunk.value);
		}
	}
	return;
}


export const importRecordingAndDbEventsFromArchive = async (
	archive: MyDataGoWhereEventArchiveFormat,
	onProgressUpdate?: (percentDone: number) => void
) => {
	const {version: _version, events, ...dbRecording} = archive;
	const eventsRemaining = [...events];
	onProgressUpdate?.(0);
	const db = await getDatabase();
	if (db == null) return;
	await db.put("recordings", dbRecording);
	onProgressUpdate?.(1);
	let written = 0;
	const chunkSize = 1000;
	while (eventsRemaining.length > 0) {
		const chunk = eventsRemaining.splice(0, chunkSize);
		const tx = db.transaction("events", "readwrite", {durability: "relaxed"});
		await Promise.all( chunk.map( chunk => tx.store.put(chunk) ) );
		tx.commit();
		written += chunk.length;
		onProgressUpdate?.(1 + Math.floor(99 * written / events.length));
	}
	
	// await Promise.all(
	// 	events.map( event => db.put("events", event) )
	// );
	onProgressUpdate?.(100);
	RecordingCreated.send({recordingUuid: dbRecording.recordingUuid});
	console.log("Wrote events", eventsRemaining.length);
}

export const writeVideoStreamsToFiles = async (
	directoryHandle: FileSystemDirectoryHandle,
	baseName: string,
	recordingUuid: UUID,
	cryptoKey: CryptoKey | undefined = undefined
): Promise<FileNameSignaturePair[]> => {
	const streamIdToFileData = new Map<string, {fileSystemFileHandle: FileSystemFileHandle, fileSystemWritableFileStream: FileSystemWritableFileStream, fileName: string}>();
	await DbVideoFragmentTableReader.forRecordingUuid(recordingUuid).forRecordingFragmentsWithAsyncCallback( async (fragment) => {
		const {streamId, data, timecode} = fragment;
		let {fileSystemWritableFileStream} = streamIdToFileData.get(streamId) ?? {};
		if (fileSystemWritableFileStream == null) {
			// We have yet to open a file for this stream
			ProcessInfo.log(`Creating file for video stream ${streamId} in recording ${recordingUuid}`);
			const priorFiles = streamIdToFileData.size;
			const fileName = `${fileNameSafeString(baseName)} Video${ priorFiles > 0 ? ` ${priorFiles + 1}` : ''}.webm`;
			const fileSystemFileHandle = await directoryHandle.getFileHandle(fileName,{ create: true });
			fileSystemWritableFileStream = await fileSystemFileHandle.createWritable({ keepExistingData: false });
			streamIdToFileData.set(streamId, {fileSystemFileHandle, fileSystemWritableFileStream, fileName});
		}
		ProcessInfo.log(`Writing ${data.size} bytes to files for stream ${streamId} at timecode ${timecode} in recording ${recordingUuid}`);
		await fileSystemWritableFileStream.write(data);
	});
	return (await Promise.all( [...streamIdToFileData.entries()].map(async ([, {fileSystemFileHandle, fileSystemWritableFileStream, fileName}]) => {
		await fileSystemWritableFileStream.close();
		const signature = cryptoKey == null ? undefined : await signFileED25519(cryptoKey, fileSystemFileHandle);
		return signature == null ? undefined : {fileName, signature} satisfies FileNameSignaturePair;
	}))).filter( x => x != null);
};

export const writeMhtmlCapturesToFiles = async (
	directoryHandle: FileSystemDirectoryHandle,
	baseName: string,
	recordingUuid: UUID,
	cryptoKey: CryptoKey | undefined = undefined
): Promise<FileNameSignaturePair[]> => {
	return ([] as FileNameSignaturePair[]).concat(...(await DbMhtmlCaptureTableReader.forRecordingUuid(await requireEvidenceDatabase(), recordingUuid)
		.mhtmlCapturesMapAsync( async (mhtmlCapture) => {
			const {timeStamp, url, mhtmlBlob} = mhtmlCapture;
			const urlObj = urlToURL(url);
			const fileNameBase = `${baseName} ${urlObj?.hostname}-${urlObj?.pathname} ${new Date(timeStamp).toISOString()}`;
			const fileName = `${fileNameSafeString(fileNameBase)}.mhtml`;
			return writeFileByNameAndSign(directoryHandle, fileName, await mhtmlBlob.arrayBuffer(), cryptoKey);
		})
	));
};

export const importRecording = async () => {
	const [fileHandle] = await window.showOpenFilePicker({ types: [{
		description: 'My Data Go Where?! Archive',
		accept: {'application/json': ['.json']},
		
	}]});
	// const file = 
	await fileHandle.getFile();
}

export const saveRecordingToDirectory = async ({licenseCache, parentDirectoryHandle, recordingUuid}: {
	licenseCache: LicenseCache,
	parentDirectoryHandle: FileSystemDirectoryHandle, recordingUuid: UUID}) => {
	const recordingReader = await DbRecordingEntryWriter.fromRecordingUuidAwaitLoad(await requireEvidenceDatabase(), recordingUuid);
	if (recordingReader == null || recordingReader.recording == null) return;
	const {recording} = recordingReader;
	const recordingName = recordingReader.getName();
	ProcessInfo.log(`Preparing to save recording ${recordingName}`);
	const directoryHandle = await parentDirectoryHandle.getDirectoryHandle(fileNameSafeString(recordingName), { create: true });
	ProcessInfo.log(`Saving recording ${recordingName} to directory ${directoryHandle.name}`);
	const [license] = licenseCache.validNonExpiredLicenses
	  .filter( x=> x?.jwkKeyForSigningThatDataWasGeneratedWithThisLicense != null);
	const {jwkKeyForSigningThatDataWasGeneratedWithThisLicense} = license ?? {};
	const signingKey = jwkKeyForSigningThatDataWasGeneratedWithThisLicense == null ? undefined :
		await crypto.subtle.importKey('jwk', jwkKeyForSigningThatDataWasGeneratedWithThisLicense, { name: 'ED25519' }, true, ['sign']);
	const fileNameSignaturePairs = ([] as FileNameSignaturePair[]).concat(...(await Promise.all([
		writeRecordingToArchiveFile(licenseCache, directoryHandle, recordingName, recording, signingKey),
		writeHarsLogToHarFile(directoryHandle, recordingName, recordingUuid, signingKey),
		writeVideoStreamsToFiles(directoryHandle, recordingName, recordingUuid, signingKey),
		writeMhtmlCapturesToFiles(directoryHandle, recordingName, recordingUuid, signingKey),
	])));
	// Write out license information
	if (license != null && fileNameSignaturePairs.length > 0) {
		const licenseFileData = {
			license,
			fileNameSignaturePairs
		} as const;
		await writeFileByNameAndSign(directoryHandle, `${fileNameSafeString(recordingName)} license.json`, JSON.stringify(licenseFileData, undefined, "\t"), signingKey)
	}
}

export const saveRecording = async ({licenseCache, recordingUuid}: {licenseCache: LicenseCache, recordingUuid: UUID}) => {
	const parentDirectoryHandle = await getDirectoryToSaveInto();
	if (parentDirectoryHandle != null) {
		await saveRecordingToDirectory({licenseCache, parentDirectoryHandle, recordingUuid});
	}
}

export const saveRecordingFn = (licenseCache: LicenseCache, recordingUuid: UUID) => () =>
	saveRecording({licenseCache, recordingUuid});

import { HarBuilder, HarEvent, isHarEventName } from "@uppajung/hardy-har";
import { DbRecordingEntryWriter } from "./DbRecordingEntryWriter";
import { getDatabase, requireEvidenceDatabase } from "./getDatabase";
import { RecordingCreated } from "../StateEvents";
import { replaceArrayBuffersWithStrings } from "../../lib/utils/arrayBuffersToBase64";
import { ProcessInfo } from "../ProcessInfo";
import { Har } from "har-format";
import { isNetworkEvent } from "../../lib/Events";
import { LicenseCache } from "./LicenseCache";
import { JSONParser } from "@streamparser/json-whatwg/index.js";
export const saveToHarFile = async (recordingUuid: UUID) => {
	const db = await requireEvidenceDatabase();
	const startIn = (await ExtensionSettingsAccessor.instance).settings.archiveDirectoryHandle;
	const recording = await db.get("recordings", recordingUuid);
	if (recording == null) return;
	const recordingName =  DbRecordingEntryReader.fromDbAndRecording(db, recording).nameOrDescription;
	const suggestedName = recordingName != null ? fileNameSafeString(recordingName).replaceAll('.','-') : undefined;
	const fileSystemFileHandle = await window.showSaveFilePicker({
		startIn,
		suggestedName,
		types: [{description: 'HTTP Archive', accept: {'application/json': ['.har']}}]}
	);
	const harBuilder = new HarBuilder({includeResourcesFromDiskCache: true, includeTextFromResponseBody: true});
	const reader = await DbEventsTableReader.forRecordingUuid(recordingUuid);
	await reader.forEvents(0, (event) => {
		if (isHarEventName(event.eventName)) {
			harBuilder.onTypedDebuggerEvent(event.eventName, event.raw as HarEvent<typeof event.eventName>);
		}
	});
	const writeStream = await fileSystemFileHandle.createWritable({ keepExistingData: false,  });
	await writeStream.write(JSON.stringify(harBuilder.getHarArchive(), undefined, "\t"));
	await writeStream.close();
}